import type { PortalJob } from './portalTypes';

/**
 * The portal's "written locally first" punch (handoff: Network error on punch). A tap
 * creates ONE PendingPunch with a fresh clientPunchID and the exact request body, stamped
 * with the tap time. It stays in memory and in sessionStorage until the server
 * acknowledges it (2xx, or a definite 4xx refusal; a 5xx keeps it, since the write may or
 * may not have landed and a resend with the same clientPunchID is safe), so:
 *   - "Retry now" and the 15 s auto-retry resend the ORIGINAL body (tap-time in/out) with
 *     the SAME clientPunchID — the API's ClientPunchID replay makes a retry of a write that
 *     actually landed return the stored row instead of a duplicate;
 *   - a reload of the tab resumes the same punch.
 * One pending punch at a time: the hero disables further punches until it is acknowledged.
 *
 * Pure apart from the injected Storage (window.sessionStorage in the app, a fake in jest).
 */

export type PunchAction = 'clockIn' | 'clockOut' | 'mealStart' | 'mealEnd' | 'jobSwitch';

export interface PendingPunch {
  clientPunchID: string;
  employeeID: number;
  action: PunchAction;
  /** What the hero and toasts call it ("Clock Out", "2nd Meal Start", "job switch"). */
  label: string;
  /** Tap time (epoch ms). The body's in/out/closeAt is this instant, never the retry time. */
  tapMs: number;
  /** The open row the punch closes (clock out / meal / switch); null for a clock in. */
  rowID: number | null;
  /** The job the new row starts on (clock in / job switch), for the optimistic hero; else null. */
  job: PortalJob | null;
  request: { method: 'post' | 'put'; url: string; body: Record<string, unknown> };
  /** 'sending' while a request is in flight; 'failed' after a network or server error (auto-retrying). */
  status: 'sending' | 'failed';
  /** Why the last attempt failed: no answer / gateway ('network') or a 5xx ('server'). */
  lastError?: 'network' | 'server';
  attempts: number;
}

export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const PENDING_PUNCH_KEY_PREFIX = 'etp.pendingPunch.';

export function pendingPunchKey(employeeID: number): string {
  return `${PENDING_PUNCH_KEY_PREFIX}${employeeID}`;
}

/** RFC 4122 v4 GUID — the format the API validates (parseClientPunchID). */
export function newClientPunchID(): string {
  const c: Crypto | undefined = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && typeof (c as { randomUUID?: () => string }).randomUUID === 'function') {
    return (c as { randomUUID(): string }).randomUUID();
  }
  // Fallback for an older browser (or jest's jsdom): still a v4-shaped GUID.
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isPendingPunch(value: unknown, employeeID: number): value is PendingPunch {
  if (!value || typeof value !== 'object') return false;
  const p = value as Partial<PendingPunch>;
  return (
    typeof p.clientPunchID === 'string' && GUID_RE.test(p.clientPunchID) &&
    p.employeeID === employeeID &&
    typeof p.tapMs === 'number' && Number.isFinite(p.tapMs) &&
    typeof p.action === 'string' &&
    typeof p.label === 'string' &&
    !!p.request && typeof p.request.url === 'string' &&
    (p.request.method === 'post' || p.request.method === 'put') &&
    !!p.request.body && typeof p.request.body === 'object'
  );
}

/** The stored pending punch for this employee, or null (absent, corrupt, or another employee's). */
export function loadPendingPunch(storage: KeyValueStorage | null, employeeID: number): PendingPunch | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(pendingPunchKey(employeeID));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!isPendingPunch(parsed, employeeID)) {
      storage.removeItem(pendingPunchKey(employeeID));
      return null;
    }
    // A reloaded tab has no request in flight, whatever the stored status said.
    return { ...parsed, status: 'failed' };
  } catch {
    return null;
  }
}

export function savePendingPunch(storage: KeyValueStorage | null, punch: PendingPunch): void {
  if (!storage) return;
  try {
    storage.setItem(pendingPunchKey(punch.employeeID), JSON.stringify(punch));
  } catch {
    /* storage full / blocked: the punch still lives in memory */
  }
}

/** Forget the pending punch — only once the server acknowledged it (or definitively refused it). */
export function clearPendingPunch(storage: KeyValueStorage | null, employeeID: number, clientPunchID?: string): void {
  if (!storage) return;
  try {
    if (clientPunchID) {
      const current = loadPendingPunch(storage, employeeID);
      // A newer punch replaced this one: leave it alone.
      if (current && current.clientPunchID !== clientPunchID) return;
    }
    storage.removeItem(pendingPunchKey(employeeID));
  } catch {
    /* ignore */
  }
}

/** Builds the pending record for a tap. The body is copied, so later edits can't change what a retry sends. */
export function createPendingPunch(args: {
  employeeID: number;
  action: PunchAction;
  label: string;
  tapMs: number;
  rowID: number | null;
  job: PortalJob | null;
  method: 'post' | 'put';
  url: string;
  body: Record<string, unknown>;
  clientPunchID?: string;
}): PendingPunch {
  const clientPunchID = args.clientPunchID ?? newClientPunchID();
  const body = JSON.parse(JSON.stringify(args.body)) as Record<string, unknown>;
  // A PUT has no idempotency key on the server (it replaces the row, so a resend is harmless);
  // POST and the switch carry it so a replay returns the stored row.
  if (args.method === 'post') body.clientPunchID = clientPunchID;
  return {
    clientPunchID,
    employeeID: args.employeeID,
    action: args.action,
    label: args.label,
    tapMs: args.tapMs,
    rowID: args.rowID,
    job: args.job,
    request: { method: args.method, url: args.url, body },
    status: 'sending',
    attempts: 0,
  };
}
